import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Eye, CheckCircle, Upload, Edit, Trash, MapPin, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FechasTab({ user, ligaId, ligaName }) {
  const [fechas, setFechas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ fecha: '', descripcion: '' });
  const [selectedFecha, setSelectedFecha] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ligaId) {
      loadData();
    }
  }, [ligaId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/fechas?liga_id=${ligaId}`, { withCredentials: true });
      setFechas(response.data);
    } catch (error) {
      toast.error('Error al cargar fechas');
    }
  };

  const handleCreateFecha = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/fechas`, { ...formData, liga_id: ligaId }, { withCredentials: true });
      toast.success('Fecha creada');
      setShowDialog(false);
      setFormData({ fecha: '', descripcion: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear fecha');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (fechaId) => {
    try {
      const response = await axios.get(`${API}/fechas/${fechaId}`, { withCredentials: true });
      setSelectedFecha(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const handleConfirmar = async (fechaId) => {
    if (!window.confirm('¿Confirmar esta fecha? No podrás hacer cambios después.')) return;
    try {
      await axios.post(`${API}/fechas/${fechaId}/confirmar`, {}, { withCredentials: true });
      toast.success('Fecha confirmada');
      loadData();
      if (selectedFecha?.id === fechaId) {
        handleViewDetail(fechaId);
      }
    } catch (error) {
      toast.error('Error al confirmar fecha');
    }
  };

  const handleExcelUpload = async (fechaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/fechas/${fechaId}/upload-excel`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      handleViewDetail(fechaId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir Excel');
    }
  };

  const copyPublicLink = (fechaId) => {
    const link = `${window.location.origin}/public/${fechaId}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado al portapapeles');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Fechas de Programacion</h2>
          <p className="text-sm text-slate-400">Liga: {ligaName}</p>
        </div>
        <Button
          data-testid="create-fecha-btn"
          onClick={() => setShowDialog(true)}
          className="gap-2 bg-white text-slate-900 hover:bg-slate-200"
        >
          <Plus className="w-4 h-4" />
          Nueva Fecha
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {fechas.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            No hay fechas creadas para esta liga
          </div>
        ) : (
          fechas.map((fecha) => (
            <Card key={fecha.id} className="p-4 hover:shadow-md transition-shadow bg-slate-800 border-slate-700" data-testid="fecha-card">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg text-white">{fecha.descripcion}</h3>
                  <p className="text-sm text-slate-400">Fecha: {fecha.fecha}</p>
                </div>
              <span
                className={`px-2 py-1 text-xs rounded ${
                  fecha.estado === 'confirmada'
                    ? 'bg-emerald-900/50 text-emerald-400'
                    : 'bg-amber-900/50 text-amber-400'
                }`}
              >
                {fecha.estado === 'confirmada' ? 'Confirmada' : 'Borrador'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid={`view-fecha-${fecha.id}`}
                onClick={() => handleViewDetail(fecha.id)}
                variant="outline"
                size="sm"
                className="gap-2 flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Eye className="w-4 h-4" />
                Ver Detalle
              </Button>
              {fecha.estado !== 'confirmada' && (
                <Button
                  data-testid={`confirm-fecha-${fecha.id}`}
                  onClick={() => handleConfirmar(fecha.id)}
                  size="sm"
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4" />
                  Confirmar
                </Button>
              )}
            </div>
            <Button
              data-testid={`copy-link-${fecha.id}`}
              onClick={() => copyPublicLink(fecha.id)}
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-slate-400 hover:text-white"
            >
              Copiar enlace público
            </Button>
          </Card>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="fecha-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Nueva Fecha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFecha} className="space-y-4">
            <div>
              <Label className="text-slate-300">Liga</Label>
              <Input value={ligaName} disabled className="bg-slate-700 border-slate-600 text-slate-400" />
            </div>
            <div>
              <Label className="text-slate-300" htmlFor="fecha">Fecha (semana)</Label>
              <Input
                data-testid="fecha-date-input"
                id="fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                required
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300" htmlFor="descripcion">Descripcion</Label>
              <Input
                data-testid="fecha-desc-input"
                id="descripcion"
                placeholder="Ej: Programacion Fecha 5 Campeonato 2025"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                required
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <Button data-testid="fecha-save-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
              {loading ? 'Creando...' : 'Crear Fecha'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {selectedFecha && (
        <FechaDetailDialog
          fecha={selectedFecha}
          open={showDetail}
          onClose={() => {
            setShowDetail(false);
            setSelectedFecha(null);
          }}
          onRefresh={() => handleViewDetail(selectedFecha.id)}
          onExcelUpload={handleExcelUpload}
        />
      )}
    </div>
  );
}

function FechaDetailDialog({ fecha, open, onClose, onRefresh, onExcelUpload }) {
  const [showCanchaDialog, setShowCanchaDialog] = useState(false);
  const [editingCancha, setEditingCancha] = useState(null);
  const [showPartidoDialog, setShowPartidoDialog] = useState(false);
  const [selectedCancha, setSelectedCancha] = useState(null);
  const [users, setUsers] = useState([]);
  const [clubes, setClubes] = useState([]);

  useEffect(() => {
    if (open) {
      loadUsers();
      loadClubes();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, { withCredentials: true });
      setUsers(response.data.filter(u => u.role === 'turno'));
    } catch (error) {
      console.error('Error loading users');
    }
  };

  const loadClubes = async () => {
    try {
      const response = await axios.get(`${API}/clubes?liga_id=${fecha.liga_id}`, { withCredentials: true });
      setClubes(response.data);
    } catch (error) {
      console.error('Error loading clubes');
    }
  };

  const handleAddCancha = () => {
    setEditingCancha(null);
    setShowCanchaDialog(true);
  };

  const handleEditCancha = (cancha) => {
    setEditingCancha(cancha);
    setShowCanchaDialog(true);
  };

  const handleAddPartido = (cancha) => {
    setSelectedCancha(cancha);
    setShowPartidoDialog(true);
  };

  const handleDeleteCancha = async (canchaId) => {
    if (!window.confirm('¿Eliminar esta cancha y todos sus partidos?')) return;
    try {
      await axios.delete(`${API}/fechas/canchas/${canchaId}`, { withCredentials: true });
      toast.success('Cancha eliminada');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cancha');
    }
  };

  const handleDeletePartido = async (partidoId) => {
    if (!window.confirm('¿Eliminar este partido?')) return;
    try {
      await axios.delete(`${API}/fechas/partidos/${partidoId}`, { withCredentials: true });
      toast.success('Partido eliminado');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar partido');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      onExcelUpload(fecha.id, file);
    }
  };

  // Get all categorias from clubes for partido selection
  const allCategorias = clubes.flatMap(club => 
    club.categorias.map(cat => ({
      ...cat,
      clubName: club.name
    }))
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700" data-testid="fecha-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">{fecha.descripcion}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {fecha.estado !== 'confirmada' && (
            <div className="flex gap-2">
              <Button
                data-testid="add-cancha-btn"
                onClick={handleAddCancha}
                className="gap-2 bg-white text-slate-900 hover:bg-slate-200"
              >
                <Plus className="w-4 h-4" />
                Agregar Cancha
              </Button>
              <label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="excel-upload-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => document.querySelector('input[type="file"]').click()}
                >
                  <Upload className="w-4 h-4" />
                  Subir Excel
                </Button>
              </label>
            </div>
          )}

          {/* Canchas List */}
          <div className="space-y-4">
            {fecha.canchas?.map((cancha) => (
              <Card key={cancha.id} className="p-4 bg-slate-700 border-slate-600" data-testid="cancha-card">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2 text-white">
                      <MapPin className="w-4 h-4 text-white" />
                      {cancha.nombre_cancha}
                    </h4>
                    <p className="text-xs text-slate-400">
                      Turno: {users.find(u => u.id === cancha.turno_user_id)?.name || 'No asignado'}
                    </p>
                  </div>
                  {fecha.estado !== 'confirmada' && (
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-cancha-${cancha.id}`}
                        onClick={() => handleEditCancha(cancha)}
                        size="sm"
                        variant="outline"
                        className="gap-2"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`add-partido-${cancha.id}`}
                        onClick={() => handleAddPartido(cancha)}
                        size="sm"
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Partido
                      </Button>
                      <Button
                        data-testid={`delete-cancha-${cancha.id}`}
                        onClick={() => handleDeleteCancha(cancha.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Partidos */}
                <div className="space-y-2">
                  {cancha.partidos?.map((partido) => (
                    <div
                      key={partido.id}
                      className="flex items-center justify-between p-2 bg-slate-700 rounded"
                      data-testid="partido-item"
                    >
                      <div className="flex-1">
                        <span className="font-medium text-sm">{partido.hora}</span>
                        <span className="mx-2 text-slate-400">-</span>
                        <span className="text-sm">
                          {partido.rival1_nombre_corto} vs {partido.rival2_nombre_corto}
                        </span>
                      </div>
                      {fecha.estado !== 'confirmada' && (
                        <Button
                          data-testid={`delete-partido-${partido.id}`}
                          onClick={() => handleDeletePartido(partido.id)}
                          size="sm"
                          variant="ghost"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Add/Edit Cancha Dialog */}
        <CanchaDialog
          open={showCanchaDialog}
          onClose={() => {
            setShowCanchaDialog(false);
            setEditingCancha(null);
          }}
          fechaId={fecha.id}
          editingCancha={editingCancha}
          users={users}
          onSuccess={onRefresh}
        />

        {/* Add Partido Dialog */}
        {selectedCancha && (
          <PartidoDialog
            open={showPartidoDialog}
            onClose={() => {
              setShowPartidoDialog(false);
              setSelectedCancha(null);
            }}
            canchaId={selectedCancha.id}
            categorias={allCategorias}
            onSuccess={onRefresh}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CanchaDialog({ open, onClose, fechaId, editingCancha, users, onSuccess }) {
  const [formData, setFormData] = useState({ nombre_cancha: '', turno_user_id: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingCancha) {
      setFormData({
        nombre_cancha: editingCancha.nombre_cancha,
        turno_user_id: editingCancha.turno_user_id || ''
      });
    } else {
      setFormData({ nombre_cancha: '', turno_user_id: '' });
    }
  }, [editingCancha, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingCancha) {
        await axios.put(`${API}/fechas/canchas/${editingCancha.id}`, formData, { withCredentials: true });
        toast.success('Cancha actualizada');
      } else {
        await axios.post(`${API}/fechas/${fechaId}/canchas`, formData, { withCredentials: true });
        toast.success('Cancha agregada');
      }
      onClose();
      setFormData({ nombre_cancha: '', turno_user_id: '' });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cancha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700" data-testid="cancha-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">{editingCancha ? 'Editar Cancha' : 'Agregar Cancha'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Nombre de la Cancha</Label>
            <Input
              data-testid="cancha-name-input"
              value={formData.nombre_cancha}
              onChange={(e) => setFormData({ ...formData, nombre_cancha: e.target.value })}
              required
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300">Turno Asignado (opcional)</Label>
            <Select 
              value={formData.turno_user_id || undefined} 
              onValueChange={(v) => setFormData({ ...formData, turno_user_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger data-testid="cancha-turno-select" className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Sin turno asignado" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                <SelectItem value="none">Sin turno</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="cancha-submit-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
            {loading ? 'Guardando...' : (editingCancha ? 'Actualizar' : 'Guardar')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PartidoDialog({ open, onClose, canchaId, categorias, onSuccess }) {
  const [formData, setFormData] = useState({ 
    hora: '', 
    rival1_nombre_corto: '', 
    rival2_nombre_corto: '',
    duracion_tiempo: 30
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/fechas/canchas/${canchaId}/partidos`, formData, { withCredentials: true });
      toast.success('Partido agregado');
      onClose();
      setFormData({ hora: '', rival1_nombre_corto: '', rival2_nombre_corto: '', duracion_tiempo: 30 });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar partido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700" data-testid="partido-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Agregar Partido</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Hora</Label>
            <Input
              data-testid="partido-hora-input"
              type="time"
              value={formData.hora}
              onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
              required
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300">Duracion por Tiempo (minutos)</Label>
            <Input
              data-testid="partido-duracion-input"
              type="number"
              min="1"
              max="90"
              value={formData.duracion_tiempo}
              onChange={(e) => setFormData({ ...formData, duracion_tiempo: parseInt(e.target.value) })}
              required
              className="bg-slate-700 border-slate-600 text-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              Ejemplo: 30 minutos = 2 tiempos de 30 min cada uno
            </p>
          </div>
          <div>
            <Label className="text-slate-300">Rival 1</Label>
            <Select value={formData.rival1_nombre_corto} onValueChange={(v) => setFormData({ ...formData, rival1_nombre_corto: v })}>
              <SelectTrigger data-testid="partido-rival1-select" className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Selecciona rival 1" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {categorias.map((cat, idx) => (
                  <SelectItem key={`${cat.categoria_id}-${idx}`} value={cat.nombre_corto}>
                    {cat.nombre_corto} ({cat.clubName})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Rival 2</Label>
            <Select value={formData.rival2_nombre_corto} onValueChange={(v) => setFormData({ ...formData, rival2_nombre_corto: v })}>
              <SelectTrigger data-testid="partido-rival2-select" className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Selecciona rival 2" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {categorias.map((cat, idx) => (
                  <SelectItem key={`${cat.categoria_id}-${idx}`} value={cat.nombre_corto}>
                    {cat.nombre_corto} ({cat.clubName})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="partido-submit-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
